///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Gates heavy dataset enable actions by their predicted GPU texture
/// footprint against the device's hard memory budget (see MemoryBudget).
/// Gating at the middleware covers every enable path - layers panel, deep
/// links, dataset browsers, camera director - before any allocation
/// happens, which on mobile Safari is the only viable strategy: once memory
/// is actually exhausted the tab is killed with no warning and no event.
///
/// The gate answers in order: fits as is -> enable; fits with the session
/// in lite mode (minimum preload windows) -> switch to lite and enable,
/// with a toast saying so; otherwise block with a toast. Degrading beats
/// refusing: the user asked for the layer and lite mode keeps the visuals.
///
/// Also keeps the crash sentinel informed of which heavy layers are on (in
/// enable order), so the launch after a crash knows what to restore and
/// which layer to suspect.

import { type Middleware } from "@reduxjs/toolkit"
import { toast } from "@/components/ui/use-toast"
import { noteHeavyLayers } from "@/earthbrowser/utils/CrashSentinel"
import { canEnableDataset, datasetDisplayName, isHeavyDataset, setLiteMode } from "@/earthbrowser/utils/MemoryBudget"
import { type DatasetType } from "@/types/DatasetElement"
import { track } from "@/utils/analytics"
import { sendDatasetAction } from "./earthBrowserSlice"

interface VisibleDatasetsState {
	earthbrowser?: { visibleDatasets?: DatasetType[] }
}

function visibleDatasets(state: unknown): DatasetType[] {
	return (state as VisibleDatasetsState).earthbrowser?.visibleDatasets ?? []
}

export const memoryGateMiddleware: Middleware = (api) => (next) => (action) => {
	if (sendDatasetAction.match(action) && action.payload.action === "enable") {
		const verdict = canEnableDataset(action.payload.type)
		if (!verdict.allowed && verdict.liteAllowed) {
			const name = datasetDisplayName(action.payload.type)
			const predictedMb = Math.round(verdict.predictedLiteBytes / 1048576)
			track("layer_enabled_lite", { layer: action.payload.type, predicted_mb: predictedMb })
			console.warn(
				`[MemoryGate] Enabling "${action.payload.type}" in lite mode: ` +
				`${Math.round(verdict.predictedBytes / 1048576)}MB predicted > ` +
				`${Math.round(verdict.hardBudget / 1048576)}MB budget, ${predictedMb}MB lite`
			)
			setLiteMode(true)
			toast({
				variant: "warning",
				title: `${name} on in reduced-memory mode`,
				description: "Memory is tight on this device, so animation lookahead is shortened for the rest of this session.",
				duration: 6000,
			})
			// Fall through: the enable proceeds with the session in lite mode
		} else if (!verdict.allowed) {
			const name = datasetDisplayName(action.payload.type)
			track("layer_blocked_memory", { layer: action.payload.type, predicted_mb: Math.round(verdict.predictedBytes / 1048576) })
			console.warn(
				`[MemoryGate] Blocked enabling "${action.payload.type}": ` +
				`${Math.round(verdict.predictedBytes / 1048576)}MB predicted > ` +
				`${Math.round(verdict.hardBudget / 1048576)}MB budget`
			)
			// Only suggest turning something off when there is a heavy layer
			// to turn off; otherwise the budget is consumed by baseline
			// rendering and the honest advice is to free the device up
			const otherHeavy = visibleDatasets(api.getState()).filter(isHeavyDataset)
			toast({
				title: `Not enough memory for ${name}`,
				description: otherHeavy.length > 0
					? `Turning on ${name} could crash this device. Turn off another layer first.`
					: `Turning on ${name} could crash this device. Close other tabs or apps and try again.`,
				duration: 6000,
			})
			// Swallow the action: the dataset stays disabled
			return action
		}
	}

	const result = next(action)

	// Checked after every action (not just dataset actions) so rehydration
	// of persisted layers and state resets are covered too; the sentinel
	// ignores unchanged sets
	noteHeavyLayers(visibleDatasets(api.getState()).filter(isHeavyDataset))
	return result
}
